import { useEffect, useRef, useState } from 'react'
import { useBlocker } from '@remix-run/react'
import type { UseFormWatch, FieldValues } from 'react-hook-form'

const normalize = (_key: string, value: unknown) =>
  value === '' || value === undefined ? null : value

export function useUnsavedChangesWarning<T extends FieldValues>(watch: UseFormWatch<T>) {
  const [dirty, setDirty] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const submittedRef = useRef(false)
  const initialValues = useRef(JSON.stringify(watch(), normalize))

  useEffect(() => {
    const subscription = watch((values) => {
      const changed = JSON.stringify(values, normalize) !== initialValues.current
      setDirty(changed)
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const isDirty = dirty && !submitted

  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && !submittedRef.current && nextLocation.pathname !== currentLocation.pathname
  )

  const markSubmitted = () => {
    submittedRef.current = true
    setSubmitted(true)
  }

  return { blocker, markSubmitted }
}
